// Libraries
import { Button, Flex, Popover, PopoverContent, PopoverTrigger, Text } from '@chakra-ui/react';
import { endOfYear, isToday, startOfYear } from 'date-fns';
import { useState } from 'react';
// Components
import { CalendarIcon, Label, RoundedArrowIcon } from '@atoms/index';
import { ColumnFilterProps, RangePicker } from '@organisms/index';
// Data
import { Tables, useDatePickerStore } from '@stores/index';
// Utils
import text from '@translations/en.json';
// Types
import { RangeDate } from '@organisms/index';
import { DateRangePickerFilterProps } from './DateRangePickerFilter.types';
import { format } from 'date-fns-tz';

/**
 * DateRangePickerFilter component for selecting a range of dates.
 * @component
 * @param {DateRangePickerFilterProps} props - The component props.
 * @returns {ReactElement} The DateRangePickerFilter component.
 */
export const DateRangePickerFilter = <TableData,>({
  props,
  smallerViews,
}: ColumnFilterProps<TableData, DateRangePickerFilterProps>) => {
  const { table } = props;
  const {
    [table]: { availableYears, initialDate, selectedDate, dateLimits },
    setDate,
  } = useDatePickerStore();

  const [minDate, maxDate] = dateLimits ?? [];
  const [dates, setDates] = useState(selectedDate);
  const [isOpen, setIsOpen] = useState(false);
  const border = isOpen ? 'selected' : 'greyCheck';
  const labelByTable =
    table === Tables.CII_FLEET_RATINGS ? text.filter_labels.period_selection : text.filter_labels.date_range;
  const label = smallerViews ? '' : labelByTable;

  const fromDateFormat = format(dates.from[0], 'MMM d, yyyy');
  const toDateFormat = isToday(dates.to[0]) ? text.today : format(dates.to[0], 'MMM d, yyyy');
  const defaultDate = {
    from: [startOfYear(new Date())],
    to: [endOfYear(new Date())],
  };

  const onSelect = (dates: RangeDate) => {
    setDates(dates);
    setIsOpen(!isOpen);
    setDate(dates, table);
  };

  return (
    <Label label={label} flex={1}>
      <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement='bottom-start'>
        <PopoverTrigger>
          <Button
            onClick={() => setIsOpen(true)}
            h={8}
            w='full'
            minW={60}
            justifyContent='space-between'
            border={border}
            variant='outline'
            bg='white'
            gap={2}
          >
            <Flex alignItems='center' pt={1}>
              <Text fontSize='m' mb={1}>
                {fromDateFormat}
              </Text>
              <Text fontSize='m' mb={1} px={2}>
                <RoundedArrowIcon fontSize='m' />
              </Text>
              <Text fontSize='m' mb={1}>
                {toDateFormat}
              </Text>
            </Flex>
            <CalendarIcon color='tealishBlue' />
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <RangePicker
            selectedDate={selectedDate}
            availableYears={availableYears}
            limits={{ minDate, maxDate }}
            defaultDate={defaultDate}
            initialDate={initialDate}
            onSelect={onSelect}
          />
        </PopoverContent>
      </Popover>
    </Label>
  );
};
